import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Trash2, Edit2, BookOpen, ChevronDown, ChevronUp, ChevronRight, GripVertical, Loader2, Check, Search, Percent, Settings, Folder, Cloud, Brain, Shield, TrendingUp, Code2, GraduationCap, Briefcase, Users, Target, Layers, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';

const CAT_ICON_MAP = { Cloud, Brain, Shield, TrendingUp, Code2, GraduationCap, Folder, BookOpen, Briefcase, Users, Target, Layers, Lightbulb, Settings };
const CAT_ICON_OPTIONS = Object.keys(CAT_ICON_MAP);

export default function CourseSyllabusPage() {
  const [syllabi, setSyllabi] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editSyllabus, setEditSyllabus] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [collapsedCats, setCollapsedCats] = useState({});
  const [showCatMgr, setShowCatMgr] = useState(false);

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.all([api.get('/courses/syllabus'), api.get('/courses/categories')])
      .then(([s, c]) => { setSyllabi(s.data.syllabi || []); setCategories(c.data.categories || []); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = async () => {
    try { await api.delete(`/courses/syllabus/${deleteId}`); setSyllabi(p => p.filter(s => s.id !== deleteId)); setDeleteId(null); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };

  const filtered = syllabi.filter(s => {
    if (filterCat !== 'all') {
      if (filterCat === '__none__' && s.category_id) return false;
      if (filterCat !== '__none__' && s.category_id !== filterCat) return false;
    }
    if (!search) return true;
    const q = search.toLowerCase();
    return s.name.toLowerCase().includes(q) || (s.description || '').toLowerCase().includes(q);
  });

  const grouped = useMemo(() => {
    const out = new Map();
    [...categories, { id: '__none__', name: 'Uncategorized', icon: 'Folder', color: '#94a3b8', __isUncat: true }].forEach(c => out.set(c.id, { cat: c, list: [] }));
    filtered.forEach(s => {
      const k = s.category_id || '__none__';
      if (!out.has(k)) out.set('__none__', out.get('__none__') || { cat: { id: '__none__', name: 'Uncategorized', icon: 'Folder', color: '#94a3b8', __isUncat: true }, list: [] });
      (out.get(k) || out.get('__none__')).list.push(s);
    });
    return out;
  }, [filtered, categories]);

  return (
    <div className="space-y-5" data-testid="course-syllabus-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2"><BookOpen className="w-5 h-5 text-violet-500" /> Course Syllabus Management</h1>
          <p className="text-xs text-slate-400 mt-0.5">{syllabi.length} course{syllabi.length !== 1 ? 's' : ''} across {categories.length} categor{categories.length !== 1 ? 'ies' : 'y'}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search courses..." className="h-8 text-xs pl-8 w-48" />
          </div>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="h-8 text-xs w-52" data-testid="cat-filter-select">
              <Folder className="w-3 h-3 mr-1 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">All Categories ({syllabi.length})</SelectItem>
              {categories.map(c => (
                <SelectItem key={c.id} value={c.id} className="text-xs">{c.name} ({syllabi.filter(s => s.category_id === c.id).length})</SelectItem>
              ))}
              <SelectItem value="__none__" className="text-xs text-slate-400">— Uncategorized ({syllabi.filter(s => !s.category_id).length})</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setShowCatMgr(true)} className="h-8 text-xs" data-testid="manage-cats-btn"><Settings className="w-3.5 h-3.5 mr-1" />Categories</Button>
          <Button onClick={() => { setEditSyllabus(null); setShowEditor(true); }} className="h-8 text-xs bg-violet-600 hover:bg-violet-700" data-testid="add-course-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add Course</Button>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : (
        <div className="space-y-3">
          {Array.from(grouped.values()).map(({ cat, list }) => {
            if (list.length === 0) return null;
            const Icon = CAT_ICON_MAP[cat.icon] || Folder;
            const collapsed = collapsedCats[cat.id];
            return (
              <Card key={cat.id} className="border overflow-hidden" data-testid={`cat-group-${cat.id}`}>
                <button
                  onClick={() => setCollapsedCats(p => ({ ...p, [cat.id]: !p[cat.id] }))}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50/50 transition-colors"
                >
                  <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ backgroundColor: cat.color + '20' }}>
                    <Icon className="w-4 h-4" style={{ color: cat.color }} />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-bold text-slate-900">{cat.name}</p>
                    <p className="text-[10px] text-slate-400">{list.length} course{list.length !== 1 ? 's' : ''}</p>
                  </div>
                  <Badge variant="outline" className="text-[10px]" style={{ backgroundColor: cat.color + '15', color: cat.color, borderColor: cat.color + '40' }}>{list.length}</Badge>
                  {collapsed ? <ChevronRight className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </button>
                {!collapsed && (
                  <div className="border-t bg-slate-50/30">
                    {list.map(s => (
                      <SyllabusCard key={s.id} syllabus={s} category={cat} expanded={expandedId === s.id} onToggle={() => setExpandedId(expandedId === s.id ? null : s.id)} onEdit={() => { setEditSyllabus(s); setShowEditor(true); }} onDelete={() => setDeleteId(s.id)} />
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center py-16 text-slate-400 text-sm">
              {syllabi.length === 0 ? 'No courses configured yet.' : 'No courses match your filter.'}
            </div>
          )}
        </div>
      )}

      <SyllabusEditor open={showEditor} syllabus={editSyllabus} categories={categories} onClose={() => { setShowEditor(false); setEditSyllabus(null); }} onSaved={fetchAll} />
      <CategoryManagerDialog open={showCatMgr} categories={categories} syllabi={syllabi} onClose={() => setShowCatMgr(false)} onChanged={fetchAll} />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Course Syllabus?</AlertDialogTitle><AlertDialogDescription>All modules and subtopics will be permanently removed.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-xs">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-xs" onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SyllabusCard({ syllabus: s, expanded, onToggle, onEdit, onDelete }) {
  const moduleCount = s.modules?.length || 0;
  const subtopicCount = (s.modules || []).reduce((sum, m) => sum + (m.subtopics?.length || 0), 0);

  return (
    <Card className="border overflow-hidden" data-testid={`syllabus-card-${s.id}`}>
      <div className="flex items-center gap-4 px-5 py-3.5 cursor-pointer hover:bg-slate-50/50" onClick={onToggle}>
        <div className="w-10 h-10 rounded-md bg-violet-50 flex items-center justify-center flex-shrink-0">
          <BookOpen className="w-5 h-5 text-violet-500" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-slate-900">{s.name}</h3>
          <p className="text-[11px] text-slate-400 truncate">{s.description || 'No description'}</p>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-slate-500">
          <Badge variant="outline" className="text-[9px]">{moduleCount} modules</Badge>
          <Badge variant="outline" className="text-[9px]">{subtopicCount} topics</Badge>
          <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-200">{s.total_weight}% total</Badge>
          {(s.keywords || []).length > 0 && (
            <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200" title={(s.keywords || []).join(', ')}>
              {s.keywords.length} keyword{s.keywords.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>
        <div className="flex gap-1" onClick={e => e.stopPropagation()}>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onEdit}><Edit2 className="w-3.5 h-3.5 text-slate-400" /></Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onDelete}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </div>
      {expanded && (
        <div className="border-t px-5 py-4 bg-slate-50/30">
          {(s.keywords || []).length > 0 && (
            <div className="mb-3 flex items-center gap-2 flex-wrap">
              <span className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Auto-link keywords:</span>
              {s.keywords.map((k, i) => (
                <Badge key={i} variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200 font-mono">{k}</Badge>
              ))}
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[9px] uppercase w-8">#</TableHead>
                <TableHead className="text-[9px] uppercase">Module</TableHead>
                <TableHead className="text-[9px] uppercase text-right">Weight %</TableHead>
                <TableHead className="text-[9px] uppercase text-right">Topics</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(s.modules || []).map((m, i) => (
                <ModuleRow key={m.id} module={m} index={i} />
              ))}
              <TableRow className="bg-slate-100/60 font-semibold">
                <TableCell />
                <TableCell className="text-xs text-slate-700">Total</TableCell>
                <TableCell className="text-xs text-right text-violet-700">{s.total_weight}%</TableCell>
                <TableCell className="text-xs text-right text-slate-600">{subtopicCount}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}

function ModuleRow({ module: m, index }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow className="cursor-pointer hover:bg-white" onClick={() => setOpen(!open)}>
        <TableCell className="text-xs text-slate-400">{index + 1}</TableCell>
        <TableCell className="text-xs font-medium text-slate-800">{m.name} {open ? <ChevronUp className="w-3 h-3 inline ml-1 text-slate-400" /> : <ChevronDown className="w-3 h-3 inline ml-1 text-slate-400" />}</TableCell>
        <TableCell className="text-xs text-right"><Badge variant="outline" className="text-[9px]">{m.weight}%</Badge></TableCell>
        <TableCell className="text-xs text-right text-slate-500">{m.subtopics?.length || 0}</TableCell>
      </TableRow>
      {open && (m.subtopics || []).map((st, j) => (
        <TableRow key={st.id} className="bg-slate-50/50">
          <TableCell />
          <TableCell className="text-[11px] text-slate-600 pl-8">{j + 1}. {st.name}</TableCell>
          <TableCell className="text-[11px] text-right text-slate-400">{st.weight}%</TableCell>
          <TableCell />
        </TableRow>
      ))}
    </>
  );
}

function SyllabusEditor({ open, syllabus, categories = [], onClose, onSaved }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [keywords, setKeywords] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [modules, setModules] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (syllabus) {
        setName(syllabus.name || '');
        setDescription(syllabus.description || '');
        setKeywords((syllabus.keywords || []).join(', '));
        setCategoryId(syllabus.category_id || '__none__');
        setModules(JSON.parse(JSON.stringify(syllabus.modules || [])));
      } else {
        setName(''); setDescription(''); setKeywords(''); setCategoryId('__none__'); setModules([]);
      }
    }
  }, [open, syllabus]);

  const addModule = () => {
    setModules(p => [...p, { id: crypto.randomUUID(), name: '', weight: 0, subtopics: [] }]);
  };
  const removeModule = (i) => setModules(p => p.filter((_, idx) => idx !== i));
  const updateModule = (i, field, val) => setModules(p => p.map((m, idx) => idx === i ? { ...m, [field]: val } : m));

  const addSubtopic = (mi) => {
    setModules(p => p.map((m, idx) => idx === mi ? { ...m, subtopics: [...(m.subtopics || []), { id: crypto.randomUUID(), name: '', weight: 0 }] } : m));
  };
  const removeSubtopic = (mi, si) => {
    setModules(p => p.map((m, idx) => idx === mi ? { ...m, subtopics: m.subtopics.filter((_, j) => j !== si) } : m));
  };
  const updateSubtopic = (mi, si, field, val) => {
    setModules(p => p.map((m, idx) => idx === mi ? { ...m, subtopics: m.subtopics.map((st, j) => j === si ? { ...st, [field]: val } : st) } : m));
  };

  const totalWeight = modules.reduce((s, m) => s + (parseFloat(m.weight) || 0), 0);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Course name required'); return; }
    setSaving(true);
    try {
      const keywordList = keywords.split(',').map(k => k.trim()).filter(Boolean);
      const data = {
        name, description,
        keywords: keywordList,
        category_id: categoryId === '__none__' ? null : categoryId,
        modules: modules.map(m => ({ ...m, weight: parseFloat(m.weight) || 0, subtopics: (m.subtopics || []).map(st => ({ ...st, weight: parseFloat(st.weight) || 0 })) })),
      };
      if (syllabus) {
        await api.put(`/courses/syllabus/${syllabus.id}`, data);
      } else {
        await api.post('/courses/syllabus', data);
      }
      toast.success(syllabus ? 'Updated' : 'Created');
      onClose(); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2"><BookOpen className="w-4 h-4 text-violet-500" />{syllabus ? 'Edit Course Syllabus' : 'Create Course Syllabus'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 mt-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium mb-1.5 block">Course Name *</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Full Stack Data Science" className="text-sm h-9" data-testid="course-name" />
            </div>
            <div>
              <Label className="text-xs font-medium mb-1.5 block">Category</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger className="h-9 text-sm" data-testid="course-category-select"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" className="text-xs text-slate-500">— Uncategorized —</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={c.id} className="text-xs">{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs font-medium mb-1.5 block">Description</Label>
            <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief description..." className="text-sm h-9" />
          </div>

          <div>
            <Label className="text-xs font-medium mb-1.5 block flex items-center gap-1.5">
              Auto-link Keywords
              <span className="text-[10px] text-slate-400 font-normal">(comma-separated — batches whose name contains any keyword will be auto-linked on CRM sync)</span>
            </Label>
            <Input value={keywords} onChange={e => setKeywords(e.target.value)} placeholder="e.g. FSDS, Python, DS_, Data Science" className="text-sm h-9" data-testid="course-keywords" />
          </div>

          {/* Modules */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Modules</Label>
                <Badge variant="outline" className={`text-[9px] ${totalWeight === 100 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : totalWeight > 100 ? 'bg-red-50 text-red-700 border-red-200' : 'text-slate-500'}`}>
                  Total: {totalWeight}% {totalWeight === 100 ? <Check className="w-2.5 h-2.5 ml-0.5 inline" /> : ''}
                </Badge>
              </div>
              <Button variant="outline" size="sm" onClick={addModule} className="h-7 text-xs" data-testid="add-module-btn"><Plus className="w-3 h-3 mr-1" />Add Module</Button>
            </div>

            <div className="space-y-3">
              {modules.map((m, mi) => (
                <Card key={m.id} className="border p-3">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="w-6 h-6 rounded bg-violet-100 text-violet-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{mi + 1}</span>
                    <Input value={m.name} onChange={e => updateModule(mi, 'name', e.target.value)} placeholder="Module name" className="text-xs h-8 flex-1" data-testid={`module-name-${mi}`} />
                    <div className="flex items-center gap-1">
                      <Input type="number" value={m.weight} onChange={e => updateModule(mi, 'weight', e.target.value)} className="text-xs h-8 w-16 text-right" data-testid={`module-weight-${mi}`} />
                      <span className="text-[10px] text-slate-400">%</span>
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeModule(mi)}><Trash2 className="w-3 h-3 text-red-400" /></Button>
                  </div>

                  {/* Subtopics */}
                  <div className="pl-9 space-y-1.5">
                    {(m.subtopics || []).map((st, si) => (
                      <div key={st.id} className="flex items-center gap-2">
                        <span className="text-[9px] text-slate-400 w-4 text-right">{si + 1}.</span>
                        <Input value={st.name} onChange={e => updateSubtopic(mi, si, 'name', e.target.value)} placeholder="Subtopic name" className="text-[11px] h-7 flex-1" />
                        <div className="flex items-center gap-1">
                          <Input type="number" value={st.weight} onChange={e => updateSubtopic(mi, si, 'weight', e.target.value)} className="text-[11px] h-7 w-14 text-right" />
                          <span className="text-[9px] text-slate-400">%</span>
                        </div>
                        <button onClick={() => removeSubtopic(mi, si)} className="p-0.5"><Trash2 className="w-2.5 h-2.5 text-red-300" /></button>
                      </div>
                    ))}
                    <Button variant="ghost" size="sm" onClick={() => addSubtopic(mi)} className="h-6 text-[10px] text-violet-600 hover:text-violet-700 px-2 mt-1">
                      <Plus className="w-2.5 h-2.5 mr-1" />Add Subtopic
                    </Button>
                    {m.subtopics?.length > 0 && (
                      <div className="text-[9px] text-slate-400 pl-4">
                        Subtopic total: {(m.subtopics || []).reduce((s, st) => s + (parseFloat(st.weight) || 0), 0)}% / Module weight: {m.weight}%
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" size="sm" onClick={onClose} className="text-xs h-9 px-4">Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving || !name.trim()} className="text-xs h-9 px-4 bg-violet-600 hover:bg-violet-700" data-testid="save-course-btn">
              {saving ? 'Saving...' : syllabus ? 'Update Course' : 'Create Course'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function CategoryManagerDialog({ open, categories, syllabi, onClose, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', icon: 'Folder', color: '#94a3b8' });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const openNew = () => { setEditing('new'); setForm({ name: '', icon: 'Folder', color: '#94a3b8' }); };
  const openEdit = (c) => { setEditing(c.id); setForm({ name: c.name, icon: c.icon, color: c.color }); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    try {
      if (editing === 'new') {
        await api.post('/courses/categories', form);
        toast.success('Category created');
      } else {
        await api.put(`/courses/categories/${editing}`, form);
        toast.success('Category updated');
      }
      setEditing(null);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/courses/categories/${confirmDelete}`);
      toast.success('Deleted');
      setConfirmDelete(null);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); setConfirmDelete(null); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { setEditing(null); onClose(); } }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2"><Settings className="w-4 h-4 text-violet-500" />Manage Course Categories</DialogTitle>
        </DialogHeader>

        {editing ? (
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs font-medium mb-1.5 block">Name *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="h-9 text-sm" placeholder="e.g. Cloud & DevOps" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium mb-1.5 block">Icon</Label>
                <Select value={form.icon} onValueChange={v => setForm(p => ({ ...p, icon: v }))}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {CAT_ICON_OPTIONS.map(name => {
                      const I = CAT_ICON_MAP[name];
                      return <SelectItem key={name} value={name}><span className="flex items-center gap-2 text-xs"><I className="w-3.5 h-3.5" />{name}</span></SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium mb-1.5 block">Colour</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} className="w-10 h-9 rounded border border-slate-200 cursor-pointer" />
                  <Input value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} className="h-9 text-xs font-mono flex-1" placeholder="#94a3b8" />
                </div>
              </div>
            </div>
            <div className="rounded-md p-3 flex items-center gap-3" style={{ backgroundColor: form.color + '15', border: `1px solid ${form.color}40` }}>
              <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ backgroundColor: form.color + '25' }}>
                {(() => { const I = CAT_ICON_MAP[form.icon] || Folder; return <I className="w-4 h-4" style={{ color: form.color }} />; })()}
              </div>
              <div>
                <p className="text-[10px] uppercase text-slate-400 tracking-wider">Preview</p>
                <p className="text-sm font-bold" style={{ color: form.color }}>{form.name || 'Category Name'}</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" size="sm" className="text-xs h-9" onClick={() => setEditing(null)}>Cancel</Button>
              <Button size="sm" className="text-xs h-9 bg-violet-600 hover:bg-violet-700" onClick={save} data-testid="save-cat-btn">{editing === 'new' ? 'Create Category' : 'Update Category'}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-1 mt-2">
            <div className="flex justify-between items-center mb-2">
              <p className="text-xs text-slate-500">{categories.length} categor{categories.length !== 1 ? 'ies' : 'y'}</p>
              <Button size="sm" className="h-7 text-xs bg-violet-600 hover:bg-violet-700" onClick={openNew} data-testid="add-cat-btn"><Plus className="w-3 h-3 mr-1" />Add Category</Button>
            </div>
            <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1">
              {categories.map(c => {
                const I = CAT_ICON_MAP[c.icon] || Folder;
                const usage = syllabi.filter(s => s.category_id === c.id).length;
                return (
                  <div key={c.id} className="flex items-center gap-3 border rounded-md px-3 py-2 hover:bg-slate-50">
                    <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: c.color + '20' }}><I className="w-4 h-4" style={{ color: c.color }} /></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{c.name}</p>
                      <p className="text-[10px] text-slate-400">{usage} course{usage !== 1 ? 's' : ''}</p>
                    </div>
                    <Badge variant="outline" className="text-[9px] font-mono" style={{ color: c.color }}>{c.color}</Badge>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(c)} data-testid={`edit-cat-${c.id}`}><Edit2 className="w-3 h-3 text-slate-400" /></Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setConfirmDelete(c.id)} data-testid={`del-cat-${c.id}`}><Trash2 className="w-3 h-3 text-red-300" /></Button>
                  </div>
                );
              })}
              {categories.length === 0 && <p className="text-xs text-slate-400 text-center py-6">No categories yet. Click "Add Category".</p>}
            </div>
          </div>
        )}
      </DialogContent>
      <AlertDialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this category?</AlertDialogTitle>
            <AlertDialogDescription>If any course uses this category, deletion will be blocked. Reassign those courses first.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-xs">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-xs" onClick={doDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
